import type { PhotographData } from "@qendo/profile/files";
import { useEffect, useState } from "react";
import { Dialog } from "react-native-paper";
import { Button } from "~/components/Button";
import { TextInput } from "~/components/form/TextInput";
import { CentredModal } from "~/components/layout/modal/CentredModal";
import { Authorize } from "~/components/permission/Authorize";
import { Gap } from "~/constants/layout";
import type { ApiError } from "~/providers/ApiProvider";
import { useErrorBag } from "~/providers/ErrorBagProvider";
import { useFeedback } from "~/providers/FeedbackProvider";
import { useGallery } from "~/providers/GalleryProvider";

export type RenameFileProps = {
    file: PhotographData;
    onRequestClose: () => void;
};

export const RenameFile = ({ file: { id, name }, onRequestClose }: RenameFileProps) => {
    const [filename, setFilename] = useState(name);
    const { saving, setSaving } = useFeedback();
    const { storeMedia } = useGallery();
    const [error, setError] = useState<string | null>(null);
    const errorBag = useErrorBag<"name">();

    // biome-ignore lint/correctness/useExhaustiveDependencies(name): update on load
    // biome-ignore lint/correctness/useExhaustiveDependencies(errorBag.forget): update on load
    useEffect(() => {
        setFilename(name);
        errorBag.forget("name");
    }, []);

    const changeName = (value: string) => {
        setError(value.trim().length === 0 ? "Name cannot be empty" : null);
        setFilename(value);
    };

    const empty = filename.trim().length === 0;

    const doSave = () => {
        if (saving || empty) {
            return;
        }

        setSaving(true);

        storeMedia({ id, name: filename })
            .then(() => setTimeout(onRequestClose, 10))
            .catch((e: ApiError) => setError(e.response?.data.message ?? e.message))
            .finally(() => setSaving(false));
    };

    return (
        <Authorize ability="update" section="photos" onCannot={onRequestClose}>
            <CentredModal
                style={[Gap.lg]}
                dismissable={!saving}
                dismissableBackButton={!saving}
                onDismiss={onRequestClose}
                visible={!saving}
            >
                <Dialog.Content>
                    <TextInput value={filename} name="name" onChangeText={changeName} label="Name" error={error} />
                </Dialog.Content>
                <Dialog.Actions style={Gap.sm}>
                    <Button onPress={onRequestClose} variant="neutral-quiet" title="Cancel" />
                    <Button onPress={doSave} variant="secondary" title="Save" disabled={empty} />
                </Dialog.Actions>
            </CentredModal>
        </Authorize>
    );
};
